import { Currency, CurrencyAmount, Price, TradeType } from '@uniswap/sdk-core'
import { SupportedChainId } from 'constants/chains'
import { WRAPPED_NATIVE_CURRENCY } from 'constants/tokens'
import { useMemo, useRef } from 'react'

import { useClientSideV3Trade } from './useClientSideV3Trade'

const HYDRA_WHYDRA = WRAPPED_NATIVE_CURRENCY[SupportedChainId.HYDRA]
const TESTNET_WHYDRA = WRAPPED_NATIVE_CURRENCY[SupportedChainId.TESTNET]

// Whydra amounts used when calculating spot price for a given currency.
const WHYDRA_AMOUNT_OUT: { [chainId: number]: CurrencyAmount<Currency> | undefined } = {
  [SupportedChainId.HYDRA]: HYDRA_WHYDRA ? CurrencyAmount.fromRawAmount(HYDRA_WHYDRA, 10e8) : undefined,
  [SupportedChainId.TESTNET]: TESTNET_WHYDRA ? CurrencyAmount.fromRawAmount(TESTNET_WHYDRA, 10e8) : undefined,
}

/**
 * Returns the price in WHYDRA of the input currency
 * @param currency currency to compute the WHYDRA price of
 */
export default function usePriceInWhydra(currency?: Currency): Price<Currency, Currency> | undefined {
  const chainId = currency?.chainId

  const amountOut = chainId ? WHYDRA_AMOUNT_OUT[chainId] : undefined
  const whydra = amountOut?.currency
  const bestV3Trade = useClientSideV3Trade(TradeType.EXACT_OUTPUT, amountOut, currency)?.trade

  const price = useMemo(() => {
    if (!currency || !whydra) {
      return undefined
    }

    // handle whydra
    if (currency?.wrapped.equals(whydra)) {
      return new Price(whydra, whydra, '1', '1')
    }

    if (bestV3Trade) {
      const { numerator, denominator } = bestV3Trade.routes[0].midPrice
      return new Price(currency, whydra, denominator, numerator)
    }

    return undefined
  }, [currency, whydra, bestV3Trade])

  const lastPrice = useRef(price)
  if (!price || !lastPrice.current || !price.equalTo(lastPrice.current)) {
    lastPrice.current = price
  }

  return lastPrice.current
}
